import { Service, Inject } from "typedi";
import { ICardinalLoadStrategy } from "./ICardinalLoadStrategy";
import { CardinalV1Loader } from "./CardinalV1Loader";
import { CardinalV2Loader } from "./CardinalV2Loader";
import { CardinalV2WithFallbackLoader } from "./CardinalV2WithFallbackLoader";
import { CardinalScriptLoader } from "./CardinalScriptLoader";
import { CardinalConnectivityTester } from "./CardinalConnectivityTester";
import { CardinalCspMonitor } from "./CardinalCspMonitor";
import { ICardinalEnvironmentConfig } from "./ICardinalEnvironmentConfig";
import { CardinalLoadMode } from "./CardinalLoadMode";
import { type IInternalsMonitor } from "../../../application/core/services/monitoring/IInternalsMonitor";

@Service()
export class CardinalLoadStrategyFactory {
  constructor(
    @Inject("IInternalsMonitor") private internalMonitor: IInternalsMonitor,
  ) {}

  create(
    mode: CardinalLoadMode,
    isLive: boolean,
    config: ICardinalEnvironmentConfig,
  ): ICardinalLoadStrategy {
    const scriptLoader = new CardinalScriptLoader();
    const connectivityTester = new CardinalConnectivityTester();
    const cspMonitor = new CardinalCspMonitor();

    switch (mode) {
      case CardinalLoadMode.V1_ONLY:
        return this.createV1Loader(scriptLoader, isLive, config);

      case CardinalLoadMode.V2_WITH_FALLBACK:
        return this.createV2WithFallback(
          scriptLoader,
          connectivityTester,
          cspMonitor,
          isLive,
          config,
        );

      case CardinalLoadMode.V2_ONLY:
        return this.createV2Loader(
          scriptLoader,
          connectivityTester,
          isLive,
          config,
        );

      default:
        throw new Error(`Unknown load mode: ${mode}`);
    }
  }

  private createV1Loader(
    scriptLoader: CardinalScriptLoader,
    isLive: boolean,
    config: ICardinalEnvironmentConfig,
  ): CardinalV1Loader {
    return new CardinalV1Loader(scriptLoader, {
      url: isLive ? config.v1.live : config.v1.test,
    });
  }

  private createV2Loader(
    scriptLoader: CardinalScriptLoader,
    connectivityTester: CardinalConnectivityTester,
    isLive: boolean,
    config: ICardinalEnvironmentConfig,
  ): CardinalV2Loader {
    const v2Config = isLive ? config.v2.live : config.v2.test;
    return new CardinalV2Loader(scriptLoader, connectivityTester, {
      url: v2Config.url,
      integrity: v2Config.sri,
      connectivityTimeout: config.connectivityTimeout,
    });
  }

  private createV2WithFallback(
    scriptLoader: CardinalScriptLoader,
    connectivityTester: CardinalConnectivityTester,
    cspMonitor: CardinalCspMonitor,
    isLive: boolean,
    config: ICardinalEnvironmentConfig,
  ): CardinalV2WithFallbackLoader {
    const v2Loader = this.createV2Loader(
      scriptLoader,
      connectivityTester,
      isLive,
      config,
    );

    const v1Loader = this.createV1Loader(scriptLoader, isLive, config);

    return new CardinalV2WithFallbackLoader(
      v2Loader,
      v1Loader,
      cspMonitor,
      this.internalMonitor,
      config.songbirdDomains,
      isLive,
    );
  }
}
